import { useEffect, useState } from 'react';

/**
 * Where Stripe Checkout sends the customer back to. Both land here rather than
 * in Teams, because Checkout runs in an external browser (it will not render in
 * the Teams iframe) and has no way to call back into the meeting.
 */
type CheckoutOutcome = 'success' | 'cancelled' | null;

function readCheckoutOutcome(): CheckoutOutcome {
  const value = new URLSearchParams(window.location.search).get('checkout');
  return value === 'success' || value === 'cancelled' ? value : null;
}

/**
 * Full-screen banner shown when the Annoteer web app loads outside a working
 * Teams meeting context — typically a direct browser visit to the production
 * URL, or a Teams "light meetings" anonymous join that strips the meeting
 * apps panel. Replaces the app shell entirely; nothing else renders behind
 * it because there is nothing usable in this state.
 *
 * It is ALSO the Stripe Checkout return page (`?checkout=success|cancelled`,
 * set as success_url / cancel_url in routes/billing.ts). That case has to be
 * acknowledged explicitly: someone who has just been charged and lands on a
 * page explaining that Annoteer runs inside Teams has no way to tell whether
 * their payment worked, and the obvious recoveries — pay again, or email
 * support — are both bad outcomes. The entitlement itself is applied by the
 * webhook and picked up in Teams on window focus; this page only has to say so.
 *
 * If we detect a light-meetings origin (referrer or URL) we tailor the body
 * copy. Otherwise we show a generic "Annoteer runs inside Teams" explainer.
 */
export function LightMeetingsBanner() {
  const [isLightMeetings, setIsLightMeetings] = useState(false);
  // Read synchronously rather than in an effect: an effect would paint the
  // generic "no standalone web experience" copy first, and that flash is
  // precisely the moment of doubt this is meant to remove.
  const [checkout] = useState<CheckoutOutcome>(readCheckoutOutcome);

  useEffect(() => {
    const ref = document.referrer.toLowerCase();
    const url = window.location.href.toLowerCase();
    if (
      ref.includes('light-meetings') ||
      ref.includes('lightexperience') ||
      url.includes('lightexperience') ||
      url.includes('anon=true')
    ) {
      setIsLightMeetings(true);
    }
  }, []);

  // global.css sets body { overflow: hidden }, so the embedded Teams panel
  // cannot scroll the whole page. This banner is NOT embedded — it is a
  // full-page view in a normal browser — and on a phone the card is taller
  // than the viewport, so that rule left the bottom permanently unreachable
  // (reported 2026-09-06: copy cut mid-word at "account, or open ten").
  // Restore scrolling while the banner is mounted, and put it back on unmount
  // so the app's own layout is untouched.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'auto';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--color-bg, #0f1117)',
        color: 'var(--color-text, #e4e4e7)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 16,
        fontFamily: 'Segoe UI, system-ui, sans-serif',
      }}
    >
      <div
        style={{
          background: 'var(--color-surface, #18191f)',
          border: '1px solid var(--color-border, rgba(255,255,255,0.08))',
          borderRadius: 12,
          padding: '32px 28px',
          maxWidth: 520,
          width: '100%',
          // Centres vertically when there is spare room, without creating the
          // unreachable overflow that align-items: center would.
          margin: 'auto',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              background: '#4F46E5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              fontWeight: 800,
              color: 'white',
              letterSpacing: '-0.02em',
            }}
            aria-hidden="true"
          >
            A
          </div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Annoteer</h1>
        </div>

        {checkout && (
          <div
            role="status"
            data-checkout={checkout}
            style={{
              background:
                checkout === 'success' ? 'rgba(34, 197, 94, 0.10)' : 'rgba(148, 163, 184, 0.10)',
              border:
                checkout === 'success'
                  ? '1px solid rgba(34, 197, 94, 0.35)'
                  : '1px solid rgba(148, 163, 184, 0.30)',
              borderRadius: 8,
              padding: 16,
              marginBottom: 20,
            }}
          >
            <h2
              style={{
                margin: '0 0 8px 0',
                fontSize: 16,
                fontWeight: 600,
                color: checkout === 'success' ? '#22c55e' : 'var(--color-text, #e4e4e7)',
              }}
            >
              {checkout === 'success' ? 'Payment received' : 'Checkout cancelled'}
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: 13,
                lineHeight: 1.6,
                color: 'var(--color-text, #e4e4e7)',
              }}
            >
              {checkout === 'success' ? (
                <>
                  Your Annoteer subscription is active. Return to your Teams meeting — the app
                  updates on its own, so there&rsquo;s nothing to reload and no need to pay again.
                  You can close this tab.
                </>
              ) : (
                <>
                  No payment was taken and you have not been charged. You can subscribe again at any
                  time from the Annoteer panel in your meeting. You can close this tab.
                </>
              )}
            </p>
          </div>
        )}

        <h2 style={{ margin: '0 0 12px 0', fontSize: 18, fontWeight: 600 }}>
          {isLightMeetings
            ? 'This meeting is using the light Teams experience'
            : 'Annoteer runs inside Microsoft Teams meetings'}
        </h2>

        <p
          style={{
            margin: '0 0 20px 0',
            fontSize: 14,
            lineHeight: 1.55,
            color: 'var(--color-text-secondary, #a1a1aa)',
          }}
        >
          {isLightMeetings ? (
            <>
              The anonymous &ldquo;light meetings&rdquo; experience that Microsoft uses for people
              without a Teams account doesn&rsquo;t support meeting apps. To use Annoteer, please
              rejoin the meeting from the full Teams desktop, mobile, or signed-in web client.
            </>
          ) : (
            <>
              Annoteer is a Microsoft Teams meeting app. To use it, open it from the apps panel
              inside a live Teams meeting. There&rsquo;s no standalone web experience —
              collaboration happens with the people in your meeting.
            </>
          )}
        </p>

        <div
          style={{
            background: 'rgba(99, 102, 241, 0.08)',
            border: '1px solid rgba(99, 102, 241, 0.25)',
            borderRadius: 8,
            padding: 16,
            marginBottom: 20,
          }}
        >
          <h3
            style={{
              margin: '0 0 10px 0',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--color-primary, #6366f1)',
            }}
          >
            How to use Annoteer
          </h3>
          <ol
            style={{
              margin: 0,
              paddingLeft: 20,
              fontSize: 13,
              lineHeight: 1.7,
              color: 'var(--color-text, #e4e4e7)',
            }}
          >
            <li>
              Sign in to{' '}
              <a
                href="https://teams.microsoft.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--color-primary, #6366f1)' }}
              >
                Microsoft Teams
              </a>{' '}
              with your work account, or open the Teams desktop or mobile app.
            </li>
            <li>Join your meeting from the full Teams client.</li>
            <li>In the meeting, open the apps panel and choose Annoteer.</li>
          </ol>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <a
            href="https://teams.microsoft.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: '#4F46E5',
              color: 'white',
              textDecoration: 'none',
              padding: '10px 18px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              display: 'inline-block',
            }}
          >
            Open Microsoft Teams
          </a>
        </div>

        <div
          style={{
            marginTop: 24,
            paddingTop: 16,
            borderTop: '1px solid var(--color-border, rgba(255,255,255,0.08))',
            fontSize: 12,
            color: 'var(--color-text-muted, #52525b)',
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <a
            href="/help"
            style={{ color: 'var(--color-text-secondary, #a1a1aa)', textDecoration: 'none' }}
          >
            Help
          </a>
          <span>·</span>
          <a
            href="/privacy"
            style={{ color: 'var(--color-text-secondary, #a1a1aa)', textDecoration: 'none' }}
          >
            Privacy
          </a>
          <span>·</span>
          <a
            href="/terms"
            style={{ color: 'var(--color-text-secondary, #a1a1aa)', textDecoration: 'none' }}
          >
            Terms
          </a>
        </div>
      </div>
    </div>
  );
}
